
import React from 'react';

export const TransactionCosts: React.FC = () => {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Understanding Transaction Costs in Apartment Sales
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Transaction costs can significantly impact your net proceeds. Understanding these costs helps you budget accurately 
            and negotiate better terms with buyers and service providers.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-start mb-16">
          <div>
            <img 
              src="https://public.readdy.ai/ai/img_res/ede2b517e44458ec982fe25145fe7a7d.jpg"
              alt="Real estate transaction costs and closing expenses breakdown"
              className="w-full h-80 object-cover rounded-2xl shadow-lg"
            />
          </div>

          <div>
            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Typical Transaction Cost Ranges</h3>
              
              <div className="space-y-4 mb-6">
                <div className="flex justify-between items-center p-4 bg-red-50 rounded-lg">
                  <span className="font-medium text-gray-900">Real Estate Commissions</span>
                  <span className="font-bold text-red-600">6-10%</span>
                </div>
                
                <div className="flex justify-between items-center p-4 bg-blue-50 rounded-lg">
                  <span className="font-medium text-gray-900">Title Insurance & Escrow</span>
                  <span className="font-bold text-blue-600">0.5-1%</span>
                </div>
                
                <div className="flex justify-between items-center p-4 bg-green-50 rounded-lg">
                  <span className="font-medium text-gray-900">Transfer Taxes</span>
                  <span className="font-bold text-green-600">0.1-2%</span>
                </div>
                
                <div className="flex justify-between items-center p-4 bg-yellow-50 rounded-lg">
                  <span className="font-medium text-gray-900">Attorney & Professional Fees</span>
                  <span className="font-bold text-yellow-600">0.5-1.5%</span>
                </div>
                
                <div className="flex justify-between items-center p-4 bg-purple-50 rounded-lg">
                  <span className="font-medium text-gray-900">Miscellaneous Costs</span>
                  <span className="font-bold text-purple-600">0.2-0.5%</span>
                </div>
              </div>

              <div className="bg-gray-900 text-white p-4 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-bold">Total Transaction Costs</span>
                  <span className="text-xl font-bold">7-15%</span>
                </div>
                <p className="text-gray-300 text-sm mt-2">
                  Of total sale price (varies by location and property type)
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 rounded-2xl p-8 mb-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">Detailed Breakdown of Transaction Costs</h3>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <h4 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <i className="ri-handshake-line text-red-500 mr-3"></i>
                Real Estate Commissions
              </h4>
              <p className="text-gray-600 mb-4">
                Typically the largest expense, paid to listing and buyer's agents. Commercial rates are often negotiable 
                and may be lower than residential rates for larger properties.
              </p>
              <div className="bg-red-50 p-4 rounded-lg">
                <div className="text-sm space-y-2">
                  <div className="flex justify-between">
                    <span>Listing Agent Commission:</span>
                    <span className="font-medium">3-5%</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Buyer's Agent Commission:</span>
                    <span className="font-medium">3-5%</span>
                  </div>
                  <div className="flex justify-between font-bold text-red-700 border-t border-red-200 pt-2">
                    <span>Total Commission:</span>
                    <span>6-10%</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <h4 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <i className="ri-shield-check-line text-blue-500 mr-3"></i>
                Title Insurance & Escrow
              </h4>
              <p className="text-gray-600 mb-4">
                Protects against title defects and handles the closing process. Costs vary by location and property value, 
                with some states having regulated rates.
              </p>
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="text-sm space-y-2">
                  <div className="flex justify-between">
                    <span>Owner's Title Policy:</span>
                    <span className="font-medium">0.3-0.6%</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Escrow Services:</span>
                    <span className="font-medium">0.1-0.3%</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Recording Fees:</span>
                    <span className="font-medium">$100-500</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <h4 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <i className="ri-government-line text-green-500 mr-3"></i>
                Transfer Taxes & Recording Fees
              </h4>
              <p className="text-gray-600 mb-4">
                Government fees for transferring property ownership. Rates vary significantly by state and municipality, 
                with some areas having no transfer tax and others charging 2% or more.
              </p>
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="text-sm space-y-2">
                  <div className="flex justify-between">
                    <span>State Transfer Tax:</span>
                    <span className="font-medium">0-1.5%</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Local Transfer Tax:</span>
                    <span className="font-medium">0-1%</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Recording Fees:</span>
                    <span className="font-medium">$50-300</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <h4 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <i className="ri-briefcase-line text-purple-500 mr-3"></i>
                Professional Services
              </h4>
              <p className="text-gray-600 mb-4">
                Legal, accounting, and consulting fees to ensure a smooth transaction. These professionals help navigate 
                complex commercial real estate laws and tax implications.
              </p>
              <div className="bg-purple-50 p-4 rounded-lg">
                <div className="text-sm space-y-2">
                  <div className="flex justify-between">
                    <span>Attorney Fees:</span>
                    <span className="font-medium">$2,000-10,000</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Accounting/Tax Advice:</span>
                    <span className="font-medium">$1,000-5,000</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Property Survey:</span>
                    <span className="font-medium">$2,000-8,000</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Cost Reduction Strategies</h3>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <i className="ri-chat-3-line text-teal-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Negotiate Commission Rates</h4>
                    <p className="text-gray-600 text-sm">
                      Commercial properties often qualify for reduced commission rates, especially on higher-value transactions.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <i className="ri-shopping-cart-2-line text-blue-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Shop Title Companies</h4>
                    <p className="text-gray-600 text-sm">
                      Title insurance rates may be negotiable, and some companies offer package deals for large transactions.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <i className="ri-calendar-check-line text-green-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Time Your Sale Strategically</h4>
                    <p className="text-gray-600 text-sm">
                      Consider tax implications and local market conditions to minimize overall transaction costs.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <i className="ri-group-line text-yellow-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Use Experienced Professionals</h4>
                    <p className="text-gray-600 text-sm">
                      Experienced commercial agents and attorneys often complete transactions more efficiently, reducing costs.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div>
            <img 
              src="https://readdy.ai/api/search-image?query=cost%20reduction%20strategies%20for%20real%20estate%20transactions%2C%20negotiation%20meeting%20between%20real%20estate%20professionals%2C%20apartment%20building%20sale%20cost%20savings%20consultation%2C%20professional%20handshake%20and%20contract%20signing&width=600&height=400&seq=cost-reduction-strategies&orientation=landscape"
              alt="Cost reduction strategies for apartment building transactions"
              className="w-full h-80 object-cover rounded-2xl shadow-lg"
            />
          </div>
        </div>
      </div>
    </section>
  );
};
